import { useCallback } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CreateEventFeature } from '@shared/constants/customPageMigration';

/**
 * Returns a function that computes the eventFeatures array based on
 * currently enabled feature flags. These features are passed to the
 * backend during event creation to configure the new event.
 */
export const useCreateEventFeatures = () => {
  const [isSunsetMomentsPageEnabled] = useIsFeatureEnabled('sunsetMomentsPageEnabled');
  const [isEngagementAlbumEnabled] = useIsFeatureEnabled('websitePhotosEngagementAlbumEnable');
  const [isPhotoMonetizationEnabled] = useIsFeatureEnabled('websitePhotoMonetizationEnabled');

  const getEventFeatures = useCallback((): ReadonlyArray<CreateEventFeature> => {
    // Theme customization restrictions will be removed for all newly created events.
    // Video-page sunset (PRNT-4148) is fully rolled out: every new event persists the
    // `sunsetVideoPageEnabled` event feature at create time, so the legacy Video page is
    // permanently hidden for it. Events created before the sunset keep the page, which is
    // why the per-event reads of this feature elsewhere in admin must stay.
    const features: Array<CreateEventFeature> = ['bypassWebsiteThemeCustomization', 'useGuestPageContentRedesign', 'sunsetVideoPageEnabled'];

    // Gated so the BE-backed default engagement album has a remote kill-switch
    // (the BE field may not be live yet — keep the flag off until it ships).
    if (isEngagementAlbumEnabled) {
      features.push('enableDefaultEngagementMediaCollection');
    }

    // Sunset: persist a per-event flag at create time so the legacy Moments page is permanently
    // hidden for new events. Decision is captured here and never re-evaluated.
    if (isSunsetMomentsPageEnabled) {
      features.push('sunsetMomentsPageEnabled');
    }

    if (isPhotoMonetizationEnabled) {
      features.push('usePhotosMonetization');
    }

    return features;
  }, [isSunsetMomentsPageEnabled, isEngagementAlbumEnabled, isPhotoMonetizationEnabled]);

  return { getEventFeatures };
};
